/**
 * Transient-error classification + retry for Supabase/PostgREST calls.
 *
 * Under DB contention (e.g. a large send flooding the webhook path), interactive
 * RPCs can fail with TRANSIENT Postgres errors — serialization failures, deadlocks,
 * lock-not-available, statement timeouts, or resource/connection-class errors — that
 * succeed on a short retry. Network errors (no `.code`) are transient too. Anything
 * with a non-transient SQLSTATE (auth/validation/business/constraint) is definitive
 * and must NOT be retried.
 *
 * Extracted from services/fieldSaleQueue.ts so all callers share one definition.
 */

// Exact transient SQLSTATEs (resource class 53xxx and connection class 08xxx are
// matched by prefix below).
export const RETRYABLE_PG_CODES = new Set([
  "40001", // serialization_failure
  "40P01", // deadlock_detected
  "55P03", // lock_not_available
  "57014", // query_canceled (statement timeout)
  "57P01", // admin_shutdown
  "57P02", // crash_shutdown
  "57P03", // cannot_connect_now
]);

/**
 * True when an error CAN succeed on retry. No `.code` → network/transient. A
 * transient SQLSTATE (the set above, or the 53xxx/08xxx classes) → retryable.
 * A non-transient code → definitive (return false).
 */
export function isTransientError(err: unknown): boolean {
  const code = (err as { code?: unknown } | null)?.code;
  if (typeof code !== "string" || code.length === 0) {
    return true; // no code → network/transient
  }
  return (
    RETRYABLE_PG_CODES.has(code) ||
    code.startsWith("53") || // insufficient_resources class
    code.startsWith("08") // connection_exception class
  );
}

export interface RetryOptions {
  retries?: number; // max RETRIES after the first attempt (default 2)
  baseDelayMs?: number; // base for exponential backoff (default 250)
  onRetry?: (err: unknown, attempt: number) => void;
}

/**
 * Run `fn`, retrying on transient errors with exponential backoff + jitter.
 * Definitive errors throw immediately. Used for thrown-error APIs.
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  opts: RetryOptions = {},
): Promise<T> {
  const retries = opts.retries ?? 2;
  const base = opts.baseDelayMs ?? 250;
  let attempt = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      return await fn();
    } catch (err) {
      attempt += 1;
      if (attempt > retries || !isTransientError(err)) {
        throw err;
      }
      opts.onRetry?.(err, attempt);
      const delay = base * 2 ** (attempt - 1) + Math.random() * base;
      await new Promise((r) => setTimeout(r, delay));
    }
  }
}

/**
 * Retry wrapper for the Supabase `{ data, error }` shape (rpc / from().insert() etc.,
 * which resolve rather than throw). Retries only when the returned `error` is transient;
 * a definitive error is returned as-is so the caller's existing error handling runs.
 */
export async function callWithRetry<T extends { error: unknown }>(
  call: () => Promise<T>,
  opts: RetryOptions = {},
): Promise<T> {
  return withRetry(async () => {
    const res = await call();
    if (res.error && isTransientError(res.error)) {
      throw res.error; // surface to withRetry so it backs off + retries
    }
    return res;
  }, opts);
}
